<template>
  <transition name="slideAside">
    <div
      v-if="onlyInvisible? true : isOpen"
      v-show="onlyInvisible ? isOpen : true"
      class="aside-filters w-full h-full bg-white right-0 shadow-aside z-50 flex flex-col dark:bg-darkblue-800"
      :class="[
        large ? 'md:w-120' : 'md:w-96', 
        footer ? 'absolute top-0' : 'fixed bottom-0 md:h-[85vh]'
        ]"
      ref="baseSlidePanel"
      tabIndex="0"
    >
      <div class="overflow-y-auto flex flex-col h-full">
        <header class="sticky top-0 flex-none z-10">
          <slot name="header" />
        </header>
        <div class=""
          :class="[
            autoHeight ? 'h-fix' : 'h-full'
          ]"
          >
          <slot name="main" />
        </div>
      </div>
      <footer v-if="footer" class="flex-none flex items-center justify-end space-x-2 px-4 bg-white dark:bg-darkblue-800 py-2 mt-auto">
        <slot name="footer" />
      </footer>
      <UtilKeys @keyup.esc="closePanel" />
    </div>
  </transition>
</template>

<script>
import UtilKeys from '@/components/utilities/UtilKeys'
import { dropdownState } from '@/state/dropdownState'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(', ')

export default {
  name: 'base-slide-panel',
  components: {
    UtilKeys
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    onlyInvisible: {
      type: Boolean,
      default: false
    },
    large: {
      type: Boolean,
      default: false
    },
    footer: {
      type: Boolean,
      default: true,
    },
    autoHeight: {
      type: Boolean,
      default: false,
    },
    // Cuando es true el panel oculta el resto de elementos de la página
    // Marcado a false mejora la usabilidad pero no es compatible con 
    // la accesibilidad (WCAG 1.3.1 / 2.4.3)
    inert: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      triggerElement: null,
      inertedElements: []
    }
  },
  watch: {
    isOpen: {
      deep: true,
      immediate: true,
      handler: 'watchisOpenHandler'
    },
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.handleFocusTrap)
    // Aseguramos restaurar el fondo si el componente se destruye estando abierto
    this.releaseBackgroundInert()
  },
  methods: {
    closePanel () {
      if (!this.isOpen) return
      // Si hay un dropdown abierto (el-select, etc.), el Escape lo ha consumido el dropdown;
      // no cerrar el panel. dropdownState permanece true ~200ms tras el cierre del dropdown.
      if (dropdownState.isAnyDropdownOpen) return
      this.$emit('closePanel')
    },
    getFocusableElements () {
      const panel = this.$refs.baseSlidePanel
      if (!panel) return []
      return Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter(el => {
        const { display, visibility } = getComputedStyle(el)
        return display !== 'none' && visibility !== 'hidden'
      })
    },
    focusFirstContentElement () {
      const panel = this.$refs.baseSlidePanel
      if (!panel) return
      const header = panel.querySelector('header')
      const first = this.getFocusableElements().find(el => !header?.contains(el))
      ;(first || panel).focus()
    },
    handleFocusTrap (event) {
      if (event.key !== 'Tab') return
      // Si hay un dropdown abierto, dejar que Tab cierre el dropdown y mueva el foco de forma natural.
      if (dropdownState.isAnyDropdownOpen) return
      const focusable = this.getFocusableElements()
      if (!focusable.length) {
        event.preventDefault()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const isInsidePanel = this.$refs.baseSlidePanel?.contains(document.activeElement)
      if (!isInsidePanel) {
        event.preventDefault()
        first.focus()
        return
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    },
    watchisOpenHandler () {
      if (this.isOpen) {
        this.triggerElement = document.activeElement
        // Ocultamos el resto de la página a productos de apoyo (WCAG 1.3.1 / 2.4.3)
        if (this.inert) {
          this.$nextTick(() => this.applyBackgroundInert())
        }
        setTimeout(() => this.focusFirstContentElement(), 500)
        document.addEventListener('keydown', this.handleFocusTrap)
      } else {
        document.removeEventListener('keydown', this.handleFocusTrap)
        this.releaseBackgroundInert()
        if (this.triggerElement && typeof this.triggerElement.focus === 'function') {
          this.triggerElement.focus()
          this.triggerElement = null
        }
      }
    },
    // Marca como inert + aria-hidden todos los hermanos de los ancestros del panel
    // (hasta <body>), dejando accesible únicamente la rama que contiene el panel.
    // De esta forma se oculta el resto de la página tanto para el teclado como
    // para los lectores de pantalla, sin destruir el estado del DOM.
    applyBackgroundInert () {
      const panel = this.$refs.baseSlidePanel
      if (!panel) return
      // Si ya hay elementos marcados por esta instancia, liberamos antes de re-aplicar
      if (this.inertedElements.length > 0) this.releaseBackgroundInert()
      const marked = []
      let node = panel
      while (node && node.parentElement && node !== document.body) {
        const parent = node.parentElement
        for (const sibling of Array.from(parent.children)) {
          if (sibling === node) continue
          // Respetamos elementos ya marcados por otro modal/panel abierto
          const alreadyInert = sibling.hasAttribute('inert')
          const hadAriaHidden = sibling.hasAttribute('aria-hidden')
          const prevAriaHidden = sibling.getAttribute('aria-hidden')
          if (!alreadyInert) sibling.setAttribute('inert', '')
          if (!hadAriaHidden) sibling.setAttribute('aria-hidden', 'true')
          marked.push({ el: sibling, alreadyInert, hadAriaHidden, prevAriaHidden })
        }
        node = parent
      }
      this.inertedElements = marked
    },
    releaseBackgroundInert () {
      if (!this.inertedElements || this.inertedElements.length === 0) return
      for (const { el, alreadyInert, hadAriaHidden, prevAriaHidden } of this.inertedElements) {
        if (!alreadyInert) el.removeAttribute('inert')
        if (hadAriaHidden) el.setAttribute('aria-hidden', prevAriaHidden)
        else el.removeAttribute('aria-hidden')
      }
      this.inertedElements = []
    }
  }
}
</script>

<style lang="scss" scoped>
.slideAside-enter-active,
.slideAside-leave-active {
  transition: all 0.15s;
  transform: translateX(0%);
  opacity: 1;
}
.slideAside-enter,
.slideAside-leave-to {
  transition: all 0.15s;
  transform: translateX(100%);
  opacity: 0;
}
.h-fix {
  height: calc(100% - 64px);
}
</style>
